import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, ExternalLink, Phone, Bookmark, BookmarkCheck, Share2, Star, ShieldCheck, Users, Building2, DollarSign, Activity, AlertTriangle, Info, Copy, Check, ChevronDown } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import RealMap from '../components/RealMap';
import ManagedCareTab from '../components/ManagedCareTab';
import PhysicianAlignmentTab from '../components/PhysicianAlignmentTab';
import HospitalAlignmentTab from '../components/HospitalAlignmentTab';
import CensusMixTab from '../components/CensusMixTab';
import PostAcuteTab from '../components/PostAcuteTab';
import ProviderIdentifierPanel from '../components/ProviderIdentifierPanel';
import { useWatchlist } from '../context/WatchlistContext';
import { api } from '../api';

const ratingTone = (r) => {
  if (r >= 5) return 'bg-[#2D5F4F] text-white';
  if (r >= 4) return 'bg-[#4A7C5E] text-white';
  if (r >= 3) return 'bg-[#D4A85C] text-[#1A2622]';
  if (r >= 2) return 'bg-[#D58668] text-white';
  if (r >= 1) return 'bg-[#C75D5D] text-white';
  return 'bg-[#9A9077] text-white';
};

function fmtMoney(n) {
  if (n == null || isNaN(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}
function fmtInt(n) {
  if (n == null) return '—';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString();
}

function Row({ k, v, sub }) {
  return (
    <div className="py-2.5 flex justify-between items-start gap-3 border-b border-[#F0E7D2] last:border-0">
      <span className="text-[12.5px] text-[#5A6660]">{k}</span>
      <div className="text-right">
        <div className="text-[12.5px] font-semibold text-[#1A2622]">{v ?? '—'}</div>
        {sub && <div className="text-[10.5px] text-[#A8B0AA] mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function Stat({ value, label, accent }) {
  return (
    <div className="p-4 rounded-lg bg-white border border-[#E5DCC4]">
      <div className={`font-display text-xl tracking-tight ${accent || 'text-[#1A2622]'}`}>{value}</div>
      <div className="mt-1 text-[10px] uppercase tracking-wider text-[#7A857F]">{label}</div>
    </div>
  );
}

// Trella-parity datasets from CMS Provider Data Catalog.
// Labels are written for a nursing-facility administrator or DON, not an analyst.
function VBPCard({ vbp }) {
  const score = vbp.performance_score;
  const scoreTier =
    score == null ? '—'
    : score >= 80 ? 'Top-tier Medicare performer'
    : score >= 60 ? 'Above average for Medicare quality'
    : score >= 40 ? 'Average Medicare performance'
    : score >= 20 ? 'Below average — improvement needed'
    : 'Bottom tier for Medicare quality';
  const scoreColor =
    score == null ? '#5A6660' : score >= 60 ? '#2D5F4F' : score >= 40 ? '#A06628' : '#7A2E0F';
  const fmtPct = (v, digits = 1) => (v == null ? '—' : `${(v * 100).toFixed(digits)}%`);
  return (
    <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]" data-testid="vbp-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#A06628]">
          <ShieldCheck size={13}/> Medicare Quality Score
        </div>
        {vbp.ranking != null && (
          <span className="text-[10.5px] text-[#7A857F]">#{vbp.ranking.toLocaleString()} nationally</span>
        )}
      </div>
      <div className="mt-4">
        <div className="font-display text-3xl tracking-tight" style={{ color: scoreColor }}>
          {score != null ? `${score.toFixed(1)} / 100` : '—'}
        </div>
        <div className="text-[13px] font-medium mt-0.5" style={{ color: scoreColor }}>{scoreTier}</div>
      </div>
      <div className="mt-4 pt-3 border-t border-[#F0E7D2]">
        <Row k="How often patients were re-admitted to hospital within 30 days"
             v={fmtPct(vbp.readmission_rate, 1)} />
        <Row k="Healthcare-associated infections (per 100 stays)"
             v={fmtPct(vbp.hai_rate, 1)} />
        <Row k="Staff turnover (nurses who left this year)"
             v={fmtPct(vbp.turnover_rate, 1)} />
        <Row k="Nursing hours each resident got per day"
             v={vbp.hprd != null ? `${vbp.hprd.toFixed(2)} hours` : '—'} />
      </div>
      <div className="mt-3 text-[11px] text-[#5A6660] leading-relaxed">
        <strong className="text-[#3A4A44]">What this is:</strong> CMS&apos;s most recent Medicare quality report for
        skilled nursing facilities. This is the latest data CMS has published — the next release comes out in
        Fall {vbp.fiscal_year ? vbp.fiscal_year + 1 : 2027}.
      </div>
    </div>
  );
}

function PBJCard({ pbj }) {
  const we = pbj.weekend_ratio;
  const weColor = we == null ? '#5A6660' : we >= 0.9 ? '#2D5F4F' : we >= 0.75 ? '#A06628' : '#7A2E0F';
  const weLabel =
    we == null ? '—'
    : we >= 0.95 ? 'Even every day'
    : we >= 0.85 ? 'Slightly lower on weekends'
    : we >= 0.70 ? 'Noticeably lower on weekends'
    : 'Much lower on weekends';
  const ctr = pbj.contract_pct;
  const ctrColor = ctr == null ? '#5A6660' : ctr <= 10 ? '#2D5F4F' : ctr <= 25 ? '#A06628' : '#7A2E0F';
  const ctrLabel =
    ctr == null ? '—'
    : ctr <= 5  ? 'Mostly in-house staff'
    : ctr <= 15 ? 'Some agency staff'
    : ctr <= 30 ? 'Heavy agency use'
    : 'Very heavy agency use';
  const q = (pbj.quarter || '').replace(/^(\d{4})Q(\d)$/, (_, y, qq) => `Q${qq} ${y}`);
  return (
    <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]" data-testid="pbj-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#2D5F4F]">
          <Users size={13}/> Real Daily Staffing
        </div>
        <span className="text-[10.5px] text-[#7A857F]">{q || 'latest quarter'} · {pbj.days_reported || 0} days</span>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3">
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">All nurses</div>
          <div className="mt-1 font-display text-xl tracking-tight text-[#1A2622]">
            {pbj.hprd_total != null ? `${pbj.hprd_total.toFixed(2)}` : '—'}
          </div>
          <div className="text-[10.5px] text-[#7A857F]">hrs / resident / day</div>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">RN only</div>
          <div className="mt-1 font-display text-xl tracking-tight text-[#1A2622]">
            {pbj.hprd_rn != null ? `${pbj.hprd_rn.toFixed(2)}` : '—'}
          </div>
          <div className="text-[10.5px] text-[#7A857F]">hrs / resident / day</div>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Aides (CNA)</div>
          <div className="mt-1 font-display text-xl tracking-tight text-[#1A2622]">
            {pbj.hprd_cna != null ? `${pbj.hprd_cna.toFixed(2)}` : '—'}
          </div>
          <div className="text-[10.5px] text-[#7A857F]">hrs / resident / day</div>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-[#F0E7D2]">
        <Row k="Average residents per day"
             v={pbj.avg_census != null ? `${pbj.avg_census.toFixed(0)} residents` : '—'} />
        <Row k="Agency / contract staff used"
             v={ctr != null ? <span style={{ color: ctrColor }}>{ctr.toFixed(1)}% · {ctrLabel}</span> : '—'} />
        <Row k="Weekend coverage"
             v={we != null ? <span style={{ color: weColor }}>{weLabel}</span> : '—'} />
      </div>
      <div className="mt-3 text-[11px] text-[#5A6660] leading-relaxed">
        <strong className="text-[#3A4A44]">What this is:</strong> Actual nursing hours reported to Medicare for every
        day of {q || 'the latest quarter'}, pulled from PBJ payroll records. A good rule of thumb is <strong className="text-[#3A4A44]">4+ hours per resident per day total</strong>, with <strong className="text-[#3A4A44]">at least 0.75 hours from an RN</strong>.
      </div>
    </div>
  );
}

function TalkingPointsSection({ ccn }) {
  const [tp, setTp] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [copied, setCopied] = React.useState(false);
  const [showWatch, setShowWatch] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    api.talkingPoints(ccn)
      .then((d) => { setTp(d); })
      .catch(() => { setTp({ ready: false, reason: 'Could not load talking points' }); })
      .finally(() => setLoading(false));
  }, [ccn]);

  const copyAll = async () => {
    if (!tp) return;
    const text =
      `${tp.facility_name} — Talking points vs ${tp.state} peers\n\n` +
      (tp.wins?.length
        ? tp.wins.map((p, i) => `${i + 1}. ${p.line}`).join('\n\n')
        : '') +
      (tp.watch?.length
        ? `\n\nAreas to watch:\n` + tp.watch.map((p) => `• ${p.line}`).join('\n')
        : '') +
      (tp.missing?.length
        ? `\n\nHospital-critical metrics CMS hasn't published for us yet:\n` +
          tp.missing.map((p) => `• ${p.label}: ${p.line}`).join('\n')
        : '');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* noop */ }
  };

  if (loading) {
    return (
      <section className="mt-7 p-5 rounded-xl bg-white border border-[#E5DCC4]" data-testid="talking-points-loading">
        <div className="text-[12px] text-[#7A857F]">Loading talking points…</div>
      </section>
    );
  }
  if (!tp?.ready) {
    return (
      <section className="mt-7 p-5 rounded-xl bg-[#F5EFDD] border border-[#D4C9AF]" data-testid="talking-points-unready">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#A06628]">
          <Info size={13}/> Talking Points
        </div>
        <p className="mt-2 text-[13px] text-[#3A4A44]">
          {tp?.reason || 'Peer comparisons will appear here as soon as the state benchmark is built.'}
        </p>
      </section>
    );
  }

  return (
    <section className="mt-7" data-testid="talking-points">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-semibold text-[#1A2622]">Talking Points</h2>
          <p className="mt-1 text-[12.5px] text-[#5A6660] max-w-2xl">
            Plain-English comparisons of this facility against the typical SNF in <strong>{tp.state}</strong>.
            Use these to brief a hospital discharge planner, a referring physician, or your own team on
            where you stand.
          </p>
        </div>
        <button onClick={copyAll} data-testid="tp-copy"
          className="h-9 px-3.5 rounded-md border border-[#D4C9AF] bg-white text-[12.5px] font-medium hover:border-[#4A7C5E] inline-flex items-center gap-1.5">
          {copied ? <><Check size={13} className="text-[#2D5F4F]"/> Copied</> : <><Copy size={13}/> Copy all</>}
        </button>
      </div>

      <div className="mt-4 p-4 rounded-md bg-[#E5F0EA] border border-[#4A7C5E]/30 text-[13px] text-[#1A2622]">
        <strong>{tp.headline}</strong>
      </div>

      {tp.wins_count > 0 && (
        <div className="mt-4 space-y-3" data-testid="tp-wins">
          {tp.wins.map((p, i) => (
            <TalkingCard key={i} point={p} tone="win" />
          ))}
        </div>
      )}

      {tp.missing_count > 0 && (
        <div className="mt-4 p-4 rounded-md bg-[#FDF6E9] border border-[#D4C9AF]" data-testid="tp-missing">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#A06628] font-semibold">
            <Info size={13}/> Hospital-critical metrics with no CMS value yet
          </div>
          <div className="mt-3 space-y-3">
            {tp.missing.map((p, i) => (
              <div key={i} className="text-[13px] text-[#3A4A44]">
                <div className="font-semibold text-[#1A2622] text-[12px] uppercase tracking-wider">{p.label}</div>
                <div className="mt-0.5 leading-relaxed">{p.line}</div>
                {p.peer_fmt && p.peer_fmt !== '—' && (
                  <div className="mt-1 text-[11.5px] text-[#5A6660]">
                    State median: <strong>{p.peer_fmt}</strong>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tp.watch_count > 0 && (
        <div className="mt-6">
          <button onClick={() => setShowWatch((s) => !s)}
            className="w-full flex items-center justify-between gap-2 p-3 rounded-md bg-[#F5EFDD] border border-[#D4C9AF] text-left hover:border-[#A06628] transition-colors"
            data-testid="tp-watch-toggle">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wider text-[#A06628] font-semibold">
                Areas to watch — {tp.watch_count} metric{tp.watch_count === 1 ? '' : 's'}
              </span>
            </div>
            <ChevronDown size={14} className={`text-[#A06628] transition-transform ${showWatch ? 'rotate-180' : ''}`} />
          </button>
          {showWatch && (
            <div className="mt-3 space-y-3" data-testid="tp-watch">
              {tp.watch.map((p, i) => (
                <TalkingCard key={i} point={p} tone="watch" />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-5 text-[11px] text-[#7A857F] leading-relaxed">
        Based on {tp.benchmark_sample_size?.toLocaleString() || 'all'} SNFs in {tp.state} using the latest CMS
        releases (Nursing Home Care Compare, SNF VBP, PBJ staffing, penalties, inspection surveys). Comparisons use
        the <strong>state median</strong> — the typical facility, not an average pulled by outliers.
      </div>
    </section>
  );
}

function TalkingCard({ point, tone }) {
  const critical = point.hospital_critical;
  const bg = critical
    ? (tone === 'win' ? 'bg-[#E5F0EA] border-[#4A7C5E]' : 'bg-[#FBE6CC] border-[#A06628]')
    : (tone === 'win' ? 'bg-white border-[#C7DDC9]' : 'bg-white border-[#E5DCC4]');
  const chipBg = tone === 'win' ? 'bg-[#E5F0EA] text-[#2D5F4F]' : 'bg-[#FBE6CC] text-[#A06628]';
  const chipText = tone === 'win'
    ? (point.pct_delta >= 20 ? `${Math.abs(point.pct_delta).toFixed(0)}% better` : 'better than peers')
    : (point.pct_delta >= 20 ? `${Math.abs(point.pct_delta).toFixed(0)}% behind` : 'below peers');
  return (
    <div className={`p-4 rounded-md border ${bg}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] uppercase tracking-wider text-[#3A4A44] font-semibold flex items-center gap-1.5">
          {critical && <span className="px-1.5 py-0.5 rounded bg-[#1A2622] text-[#FBF6EC] text-[9px] tracking-widest">#{point.priority}</span>}
          {point.label}
        </div>
        <span className={`text-[10.5px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${chipBg}`}>
          {chipText}
        </span>
      </div>
      <div className="mt-1 text-[13.5px] text-[#1A2622] leading-relaxed">{point.line}</div>
      <div className="mt-2 flex items-center gap-4 text-[11.5px] text-[#5A6660]">
        <span><span className="text-[#7A857F]">You:</span> <strong className="text-[#1A2622]">{point.ours_fmt}</strong></span>
        <span><span className="text-[#7A857F]">State median:</span> {point.peer_fmt}</span>
      </div>
    </div>
  );
}

function ReferringHospitalsSection({ ccn }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    setLoading(true);
    api.facilityHospitals(ccn, { limit: 10 })
      .then((d) => setData(d))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [ccn]);

  if (loading) return (
    <section className="mt-7 p-5 rounded-xl bg-white border border-[#E5DCC4]" data-testid="refer-hosp-loading">
      <div className="text-[12px] text-[#7A857F]">Loading nearby hospitals…</div>
    </section>
  );
  const items = (data?.items || []).slice(0, 5);
  if (!items.length) return null;

  return (
    <section className="mt-7" data-testid="referring-hospitals">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-semibold text-[#1A2622]">Hospitals That May Send You Referrals</h2>
          <p className="mt-1 text-[12.5px] text-[#5A6660] max-w-2xl">
            Hospitals within ~25 miles ranked by shared-physician overlap. Each card shows the top conditions
            that hospital discharges — the kind of patients they&apos;re looking for a SNF to take.
          </p>
        </div>
      </div>
      <div className="mt-4 space-y-3">
        {items.map((h, i) => (
          <HospitalReferralCard key={h.ccn_hospital || i} h={h} />
        ))}
      </div>
      <div className="mt-3 text-[11px] text-[#7A857F] leading-relaxed">
        Data: CMS Hospital General Info + Facility Affiliation + Medicare Inpatient Hospitals by Provider &amp;
        Service (CY 2023). <strong>Not patient-level claims</strong> — proximity + physician overlap, not actual
        referral records.
      </div>
    </section>
  );
}

function HospitalReferralCard({ h }) {
  const score = Math.round(h.alignment_score || 0);
  const scoreColor = score >= 75 ? '#2D5F4F' : score >= 50 ? '#A06628' : '#5A6660';
  const d = h.discharges;
  const top = (d?.top_drgs || []).slice(0, 3);
  return (
    <div className="p-4 rounded-md bg-white border border-[#E5DCC4]">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <a href={`/hospitals/${h.ccn_hospital}`}
             className="text-[14px] font-semibold text-[#1A2622] hover:text-[#2D5F4F]"
             data-testid={`refer-hosp-${h.ccn_hospital}`}>
            {h.hospital_name || h.name_hospital || 'Unknown hospital'}
          </a>
          <div className="mt-0.5 text-[11.5px] text-[#5A6660]">
            {h.hospital_city && <>{h.hospital_city}, {h.hospital_state} · </>}
            {h.distance_mi != null && <>{h.distance_mi.toFixed(1)} mi away · </>}
            {h.hospital_rating ? <>{h.hospital_rating}★ CMS rating</> : null}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Alignment</span>
          <span className="font-display text-xl tracking-tight" style={{ color: scoreColor }}>{score}</span>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-[12px]">
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Shared physicians</div>
          <div className="mt-0.5 font-semibold text-[#1A2622]">
            {h.confirmed_shared_physicians || 0} confirmed
            {h.likely_shared_physicians ? <span className="text-[#7A857F]"> · +{h.likely_shared_physicians} likely</span> : null}
          </div>
        </div>
        {d && (
          <div>
            <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Yearly Medicare discharges</div>
            <div className="mt-0.5 font-semibold text-[#1A2622]">
              {d.total_discharges?.toLocaleString() || '—'}
              <span className="text-[11px] text-[#7A857F] ml-1">({d.source_period})</span>
            </div>
          </div>
        )}
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F]">Confidence</div>
          <div className="mt-0.5">
            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${h.confidence === 'high' ? 'bg-[#E5F0EA] text-[#2D5F4F]' : h.confidence === 'medium' ? 'bg-[#FBE6CC] text-[#A06628]' : 'bg-[#F0E7D2] text-[#5A6660]'}`}>
              {h.confidence || 'low'}
            </span>
          </div>
        </div>
      </div>
      {top.length > 0 && (
        <div className="mt-3 pt-3 border-t border-[#F0E7D2]">
          <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F] font-semibold">
            Top conditions they discharge
          </div>
          <ol className="mt-2 space-y-1">
            {top.map((t, i) => (
              <li key={t.drg || i} className="flex items-start justify-between gap-3 text-[12.5px]">
                <span className="text-[#1A2622] flex-1">
                  <span className="text-[#7A857F] mr-1.5">#{i + 1}</span>
                  {t.category ? <span className="font-semibold">{t.category}</span> : t.name}
                  {t.category && t.name && <span className="text-[#7A857F]"> — {t.name}</span>}
                </span>
                <span className="font-semibold tabular-nums text-[#1A2622] whitespace-nowrap">
                  {t.discharges?.toLocaleString()} <span className="text-[11px] text-[#7A857F] font-normal">cases/yr</span>
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

export default function FacilityDetailPage() {
  const { slug } = useParams();
  const [f, setF] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [hospLinks, setHospLinks] = useState(null);
  const { isWatched, toggle } = useWatchlist();

  useEffect(() => {
    setLoading(true);
    api.facility(slug).then((d) => {
      setF(d); setLoading(false);
      try {
        window.gtag && window.gtag('event', 'profile_view', {
          entity_type: 'facility', ccn: d?.ccn, state: d?.state,
          chain: d?.chain_name || null, rating: (d?.ratings || {}).overall || null,
        });
      } catch (_) {}
    }).catch(() => setLoading(false));
    // Hospital links for the map overlay (top 5 closest by alignment score)
    api.facilityHospitalLinks(slug, 5).then(setHospLinks).catch(() => setHospLinks(null));
  }, [slug]);

  if (loading) return <div className="min-h-screen"><Navbar /><main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10"><div className="text-sm text-[#7A857F]">Loading facility…</div></main></div>;
  if (!f) return <div className="min-h-screen"><Navbar /><main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-10"><h1 className="font-display text-3xl">Facility not found</h1></main></div>;

  const watched = isWatched('facility', f.ccn);
  const overall = (f.ratings || {}).overall || 0;
  const fin = f.financials;
  const pen = f.penalties;
  const sur = f.survey;
  const margin = fin && fin.net_patient_revenue ? ((fin.net_income / fin.net_patient_revenue) * 100).toFixed(1) : null;
  const occCalc = fin && fin.bed_days_available ? ((fin.total_days / fin.bed_days_available) * 100).toFixed(1) : null;
  const markers = f.latitude && f.longitude ? [{ ccn: f.ccn, name: f.name, city: f.city, state: f.state, lat: f.latitude, lng: f.longitude, rating: overall }] : [];

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'financials', label: 'Financials' },
    { id: 'surveys', label: 'Surveys & Fines' },
    { id: 'managed-care', label: 'Managed Care' },
    { id: 'physicians', label: 'Physician Alignment' },
    { id: 'hospitals', label: 'Hospital Network' },
    { id: 'post-acute', label: 'Post-Acute Network' },
    { id: 'census', label: 'Census & Payer Mix' },
    { id: 'ownership', label: `Ownership (${(f.owners || []).length})` },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-10">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Facilities', to: '/facilities' }, { label: f.name }]} />

        {/* Header */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#4A7C5E]/30 bg-white">
              <Building2 size={12} className="text-[#4A7C5E]"/>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">CMS Facility · CCN {f.ccn}</span>
            </div>
            {f.npi && (
              <span className="ml-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#34538F]/30 bg-white" data-testid="facility-header-npi">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#34538F]">Org NPI</span>
                <span className="text-[11px] font-mono tracking-tight text-[#1A2622]">{f.npi}</span>
              </span>
            )}
            <h1 className="mt-3 font-display text-4xl md:text-5xl font-semibold text-[#1A2622] tracking-tight leading-tight">{f.name}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-[#3A4A44]">
              <span className="flex items-center gap-1.5"><MapPin size={13} className="text-[#4A7C5E]"/> {f.address}, {f.city}, {f.state} {f.zip}</span>
              {f.phone && <span className="flex items-center gap-1.5"><Phone size={13} className="text-[#4A7C5E]"/> {f.phone}</span>}
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((f.address || '') + ' ' + (f.city || '') + ' ' + (f.state || ''))}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#4A7C5E] hover:underline">Open in Maps <ExternalLink size={11}/></a>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <button onClick={() => toggle({ kind: 'facility', slug: f.ccn, name: f.name, city: f.city, state: f.state })} className={`inline-flex items-center gap-1.5 px-3.5 h-9 rounded-md text-[12.5px] font-medium ${watched ? 'bg-[#4A7C5E] text-white' : 'bg-[#1A2622] text-[#FBF6EC] hover:bg-[#2D5F4F]'}`}>
                {watched ? <><BookmarkCheck size={13}/> Watching</> : <><Bookmark size={13}/> Watch</>}
              </button>
              <button className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-md bg-white border border-[#E5DCC4] text-[12.5px] text-[#3A4A44] hover:border-[#4A7C5E]"><Share2 size={13}/> Share</button>
            </div>
          </div>
          <div className="lg:col-span-4 self-start">
            <div className={`rounded-xl p-5 flex items-center gap-5 ${ratingTone(overall)}`}>
              <div className="font-display text-5xl font-semibold">{overall || '—'}</div>
              <div>
                <div className="text-[10px] uppercase tracking-wider opacity-80">Overall rating</div>
                <div className="mt-1 flex items-center gap-0.5">
                  {[1,2,3,4,5].map(i => <Star key={i} size={12} fill={i <= overall ? 'currentColor' : 'none'} strokeWidth={1.5}/>)}
                </div>
                <div className="text-[10.5px] mt-1 opacity-90">CMS Five-Star</div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats strip */}
        <section className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat value={f.beds ?? '—'} label="Certified beds"/>
          <Stat value={f.residents ?? '—'} label="Residents"/>
          <Stat value={f.occupancy != null ? f.occupancy + '%' : '—'} label="Occupancy"/>
          <Stat value={f.ownership_type || '—'} label="Ownership"/>
          <Stat value={f.chain_name || '—'} label="Chain"/>
        </section>

        {/* Provider Identifier & Entity Intelligence (additive — NPPES enrichment) */}
        <div className="mt-5">
          <ProviderIdentifierPanel
            entityType="facility"
            entityId={f.ccn}
            fallback={{ ccn: f.ccn, ownership_type: f.ownership_type, chain_name: f.chain_name, legal_name: f.legal_name }}
          />
        </div>

        {/* Tabs */}
        <div className="mt-8 border-b border-[#E5DCC4]">
          <div className="flex items-center gap-1 overflow-x-auto">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} className={`shrink-0 px-4 py-3 text-[13px] border-b-2 transition-colors ${tab === t.id ? 'border-[#4A7C5E] text-[#1A2622] font-semibold' : 'border-transparent text-[#7A857F] hover:text-[#1A2622]'}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {tab === 'overview' && (
          <section className="mt-7 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><ShieldCheck size={13}/> Ratings (1–5)</div>
              <div className="mt-3">
                <Row k="Overall" v={f.ratings?.overall}/>
                <Row k="Health inspection" v={f.ratings?.health_inspection}/>
                <Row k="Staffing" v={f.ratings?.staffing}/>
                <Row k="Quality measures" v={f.ratings?.quality_measures}/>
                <Row k="Recent deficiencies" v={f.recent_deficiencies}/>
                <Row k="Last survey" v={f.last_survey_date}/>
              </div>
            </div>
            <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><Users size={13}/> Staffing (HPRD)</div>
              <div className="mt-3">
                <Row k="Total nurse" v={f.staffing?.total_nurse_hprd}/>
                <Row k="RN" v={f.staffing?.rn_hprd}/>
                <Row k="Weekend nurse" v={f.staffing?.weekend_hprd}/>
              </div>
            </div>
            <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><DollarSign size={13}/> Financial snapshot</div>
              <div className="mt-3">
                <Row k="Net patient revenue" v={fmtMoney(fin?.net_patient_revenue)}/>
                <Row k="Net income" v={fmtMoney(fin?.net_income)}/>
                <Row k="Total fines" v={fmtMoney(pen?.total_fines)}/>
                <Row k="Health deficiencies" v={sur?.total_health_deficiencies ?? '—'}/>
              </div>
            </div>
          </section>
        )}

        {tab === 'overview' && (f.vbp || f.pbj) && (
          <section className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="overview-trella">
            {f.vbp && <VBPCard vbp={f.vbp} />}
            {f.pbj && <PBJCard pbj={f.pbj} />}
          </section>
        )}

        {tab === 'overview' && <TalkingPointsSection ccn={f.ccn} />}
        {tab === 'overview' && <ReferringHospitalsSection ccn={f.ccn} />}

        {tab === 'financials' && (
          <section className="mt-7">
            {fin ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Stat value={fmtMoney(fin.net_patient_revenue)} label="Net patient revenue"/>
                  <Stat value={fmtMoney(fin.net_income)} label="Net income" accent="text-[#4A7C5E]"/>
                  <Stat value={occCalc ? `${occCalc}%` : '—'} label="Cost report occupancy"/>
                  <Stat value={margin ? `${margin}%` : '—'} label="Operating margin"/>
                </div>
                <div className="mt-4 p-5 rounded-xl bg-white border border-[#E5DCC4]">
                  <div className="grid grid-cols-1 md:grid-cols-5 items-center gap-4 text-center">
                    <div><div className="font-display text-xl text-[#1A2622]">{fmtMoney(fin.net_patient_revenue)}</div><div className="text-[10px] uppercase tracking-wider text-[#7A857F] mt-1">Revenue</div></div>
                    <div className="text-xl text-[#C9C0A8]">−</div>
                    <div><div className="font-display text-xl text-[#1A2622]">{fmtMoney(fin.operating_expense)}</div><div className="text-[10px] uppercase tracking-wider text-[#7A857F] mt-1">Op. expense</div></div>
                    <div className="text-xl text-[#C9C0A8]">=</div>
                    <div><div className="font-display text-xl text-[#4A7C5E]">{fmtMoney(fin.net_income)}</div><div className="text-[10px] uppercase tracking-wider text-[#4A7C5E] mt-1">Net income</div></div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><DollarSign size={13}/> Balance sheet</div>
                    <div className="mt-3">
                      <Row k="Total assets" v={fmtMoney(fin.total_assets)}/>
                      <Row k="Current assets" v={fmtMoney(fin.total_current_assets)}/>
                      <Row k="Fixed assets" v={fmtMoney(fin.total_fixed_assets)}/>
                      <Row k="Total liabilities" v={fmtMoney(fin.total_liabilities)}/>
                      <Row k="Fund balances" v={fmtMoney(fin.fund_balances)}/>
                    </div>
                  </div>
                  <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><Activity size={13}/> Operations</div>
                    <div className="mt-3">
                      <Row k="Resident days" v={fmtInt(fin.total_days)}/>
                      <Row k="Bed days available" v={fmtInt(fin.bed_days_available)}/>
                      <Row k="Total discharges" v={fmtInt(fin.total_discharges)}/>
                      <Row k="SNF admissions" v={fmtInt(fin.snf_admissions)}/>
                      <Row k="Total costs" v={fmtMoney(fin.total_costs)}/>
                    </div>
                  </div>
                  <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><ShieldCheck size={13}/> Payer mix</div>
                    <div className="mt-3 mb-3 h-3 w-full rounded-full overflow-hidden bg-[#F0E7D2] flex">
                      <div className="bg-[#4A7C5E]" style={{ width: fin.total_days ? `${(fin.medicare_days / fin.total_days) * 100}%` : '0%' }}/>
                      <div className="bg-[#7BA88E]" style={{ width: fin.total_days ? `${(fin.medicaid_days / fin.total_days) * 100}%` : '0%' }}/>
                      <div className="bg-[#D4A85C]" style={{ width: fin.total_days ? `${(fin.other_days / fin.total_days) * 100}%` : '0%' }}/>
                    </div>
                    <Row k={<span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#4A7C5E]"/>Medicare</span>} v={`${fmtInt(fin.medicare_days)} (${fin.total_days ? ((fin.medicare_days / fin.total_days) * 100).toFixed(1) : 0}%)`}/>
                    <Row k={<span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#7BA88E]"/>Medicaid</span>} v={`${fmtInt(fin.medicaid_days)} (${fin.total_days ? ((fin.medicaid_days / fin.total_days) * 100).toFixed(1) : 0}%)`}/>
                    <Row k={<span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#D4A85C]"/>Other</span>} v={`${fmtInt(fin.other_days)} (${fin.total_days ? ((fin.other_days / fin.total_days) * 100).toFixed(1) : 0}%)`}/>
                  </div>
                </div>
                <p className="mt-5 text-[11.5px] text-[#A8B0AA]">Source: CMS SNF Cost Report public dataset. Fiscal year ending {fin.fy_end || 'most recent'}.</p>
              </>
            ) : (
              <div className="rounded-xl bg-white border border-dashed border-[#E5DCC4] p-8 text-center">
                <DollarSign size={22} className="text-[#A8B0AA] mx-auto"/>
                <div className="mt-3 text-[13px] text-[#7A857F]">No cost report on file for this facility yet.</div>
              </div>
            )}
          </section>
        )}

        {tab === 'surveys' && (
          <section className="mt-7">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat value={fmtMoney(pen?.total_fines)} label="Total federal fines" accent="text-[#C75D5D]"/>
              <Stat value={pen?.penalty_count ?? '—'} label="Penalty events"/>
              <Stat value={sur?.total_health_deficiencies ?? '—'} label="Health deficiencies"/>
              <Stat value={sur?.total_fire_deficiencies ?? '—'} label="Fire safety defs"/>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><DollarSign size={13}/> Federal penalties</div>
                <div className="mt-3">
                  <Row k="Total fines" v={fmtMoney(pen?.total_fines)}/>
                  <Row k="Number of fines" v={pen?.fine_count ?? '—'}/>
                  <Row k="Payment denials" v={pen?.denial_count ?? '—'}/>
                  <Row k="Total penalty events" v={pen?.penalty_count ?? '—'}/>
                  <Row k="Latest penalty" v={pen?.latest_penalty || '—'}/>
                </div>
              </div>
              <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><ShieldCheck size={13}/> Health deficiencies</div>
                <div className="mt-3">
                  <Row k="Total health" v={sur?.total_health_deficiencies ?? '—'}/>
                  <Row k="Abuse, neglect, exploitation" v={sur?.abuse_neglect_deficiencies ?? '—'}/>
                  <Row k="Infection control" v={sur?.infection_control_deficiencies ?? '—'}/>
                  <Row k="Resident rights" v={sur?.resident_rights_deficiencies ?? '—'}/>
                  <Row k="Quality of life & care" v={sur?.quality_of_life_deficiencies ?? '—'}/>
                </div>
              </div>
              <div className="p-5 rounded-xl bg-white border border-[#E5DCC4]">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#4A7C5E]"><AlertTriangle size={13}/> Survey context</div>
                <div className="mt-3">
                  <Row k="Last health survey" v={sur?.health_survey_date || '—'}/>
                  <Row k="Total fire safety defs" v={sur?.total_fire_deficiencies ?? '—'}/>
                  <Row k="Recent CMS deficiencies" v={f.recent_deficiencies ?? '—'}/>
                </div>
              </div>
            </div>

            {(f.recent_penalty_events || []).length > 0 && (
              <div className="mt-5">
                <h3 className="font-display text-base font-semibold text-[#1A2622] mb-3">Recent penalty events</h3>
                <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
                  <table className="w-full text-[12.5px]">
                    <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
                      <tr><th className="text-left px-4 py-2.5">Date</th><th className="text-left px-4 py-2.5">Type</th><th className="text-right px-4 py-2.5">Amount</th></tr>
                    </thead>
                    <tbody>
                      {f.recent_penalty_events.map((p, i) => (
                        <tr key={i} className="border-t border-[#F0E7D2]">
                          <td className="px-4 py-2.5 text-[#3A4A44]">{p.penalty_date || '—'}</td>
                          <td className="px-4 py-2.5">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${p.penalty_type === 'Fine' ? 'bg-[#FBE6E0] text-[#C75D5D]' : 'bg-[#F5EFDD] text-[#3A4A44]'}`}>{p.penalty_type || '—'}</span>
                          </td>
                          <td className="px-4 py-2.5 text-right font-semibold text-[#1A2622]">{p.penalty_type === 'Fine' ? fmtMoney(p.fine_amount) : (p.payment_denial_days ? `${p.payment_denial_days} days` : '—')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <p className="mt-5 text-[11.5px] text-[#A8B0AA]">Source: CMS Nursing Home Penalties + Survey Summary public datasets.</p>
          </section>
        )}

        {tab === 'managed-care' && <ManagedCareTab ccn={f.ccn} />}

        {tab === 'physicians' && <PhysicianAlignmentTab ccn={f.ccn} />}

        {tab === 'hospitals' && <HospitalAlignmentTab ccn={f.ccn} facility={f} />}

        {tab === 'post-acute' && <PostAcuteTab kind="facility" ccn={f.ccn} />}

        {tab === 'census' && <CensusMixTab ccn={f.ccn} />}

        {tab === 'ownership' && (
          <section className="mt-7">
            <div className="rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
              {(f.owners || []).length === 0 ? (
                <div className="p-6 text-center text-[13px] text-[#7A857F]">No ownership records associated.</div>
              ) : (
                <ul>
                  {f.owners.map((o, i) => {
                    const slug = (o.owner_name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 120);
                    return (
                      <li key={i}>
                        <Link to={`/owners/${slug}`} className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[#F0E7D2] first:border-t-0 hover:bg-[#FBF6EC]">
                          <div className="min-w-0">
                            <div className="text-[13.5px] font-medium text-[#1A2622] truncate">{o.owner_name}</div>
                            <div className="text-[10.5px] text-[#7A857F] mt-0.5">{o.role || ''}</div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            <span className="text-[10px] text-[#7A857F]">{o.owner_type || ''}</span>
                            {o.ownership_pct && <span className="text-[11px] font-semibold text-[#1A2622]">{o.ownership_pct}</span>}
                          </div>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>
        )}

        {f.latitude && f.longitude && (
          <section className="mt-8">
            <div className="flex items-baseline justify-between gap-3 mb-3">
              <h2 className="font-display text-xl font-semibold text-[#1A2622]">Location & referral network</h2>
              {hospLinks?.items?.length > 0 && (
                <span className="text-[11.5px] text-[#7A857F]">{hospLinks.items.length} nearby hospitals shown · color = match confidence</span>
              )}
            </div>
            <RealMap
              markers={markers}
              height={360}
              center={[f.latitude, f.longitude]}
              zoom={11}
              hospitals={(hospLinks?.items || []).map((i) => ({
                ccn: i.ccn_hospital, name: i.hospital_name,
                lat: i.lat, lng: i.lng, rating: i.hospital_rating, state: f.state, city: '',
              }))}
              hospitalLinks={(hospLinks?.items || []).map((i) => ({
                from: { lat: hospLinks.facility.lat, lng: hospLinks.facility.lng },
                to: { lat: i.lat, lng: i.lng },
                confidence: i.confidence,
                distance_mi: i.distance_mi,
                score: i.alignment_score,
                label: `${hospLinks.facility.name} ↔ ${i.hospital_name}`,
              }))}
              showHospitalsToggle
              defaultHospitalsOn
            />
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
